import { redirectTo } from "../features/navigation/navigation.ts";
import { t } from "../features/store-language/i18n.ts";
import { FormInstance } from "antd";
import axios from "axios";
import { getLanguage } from "../features/store-language/i18n.ts";
import AppFeedback from "../components/shared/AppFeedback.tsx";
import saveAs from "file-saver";
import { hasJwtRole } from "./jwtRoles.ts";
import { sanitizeUnhandledHttpError } from "./appError.ts";
import { getAgencySession, setAgencySession } from "./agencySession.ts";

type AgencyBootstrap = { isAuthenticated: boolean; csrfToken: string };
let catalogCsrfToken = "";
let catalogCsrfPromise: Promise<string> | null = null;

export class HttpClientService {
  private instance: any;
  private baseurl: string;

  constructor(baseURL: string) {
    const localBaseURL = this.getLocalBaseURL(baseURL);
    this.baseurl = localBaseURL;
    this.instance = axios.create({ baseURL: localBaseURL, withCredentials: true });
    this.instance.interceptors.request.use(
      async (config) => {
        const token = localStorage.getItem("jwt");
        if (String(config.url).startsWith("/catalog/")) {
          config.headers["Accept-Language"] = getLanguage();
          const session = getAgencySession();
          if (session && /^\/catalog\/orders(\/|$)/.test(String(config.url))) {
            config.headers["X-Portal-Membership"] = session.membershipId;
          }
        }

        if (token && !String(config.url).startsWith("/identity/") && !String(config.url).startsWith("/catalog/")) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }

        if (/^\/catalog\/(orders|favorites|notifications)(\/|$)/.test(String(config.url))
            && !["get", "head", "options"].includes(String(config.method).toLowerCase())) {
          config.headers["X-Portal-Csrf"] = await this.catalogCsrf();
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    this.instance.interceptors.response.use(
      (response) => {
        // A completed read from an old locale must not overwrite the new locale's data.
        if (response.config.method === "get" && String(response.config.url).startsWith("/catalog/")
            && response.config.headers["Accept-Language"] !== getLanguage()) {
          return this.instance.request(response.config);
        }
        return response;
      },
      async (error) => {
        sanitizeUnhandledHttpError(error);
        if (error.traceId) {
          void AppFeedback.httpError(error);
        }
        const status = error.response ? error.response.status : null;
        const requestUrl = error.config?.url ?? "";
        const isLoginRequest =
          requestUrl.startsWith("/catalog/agency-account/") ||
          requestUrl.startsWith("/catalog/agency-registration") ||
          requestUrl.startsWith("/identity/") ||
          (requestUrl.startsWith("/catalog/account/") && requestUrl !== "/catalog/account/profile") ||
          requestUrl.includes("/user/login") ||
          requestUrl.includes("/CustomerIdentity/CustomerLogin") ||
          requestUrl.includes("/CustomerIdentity/SendOtp") ||
          requestUrl.includes("/CustomerIdentity/OtpLogin") ||
          requestUrl.includes("/CustomerIdentity/OtpSettings") ||
          requestUrl.includes("/CustomerIdentity/RegisterCustomer");

        if (status === 401 && !isLoginRequest) {
          if (requestUrl.startsWith("/catalog/")) setAgencySession(null);
          const pathname = window.location.pathname;
          const isCustomerContext =
            pathname === "/" ||
            /^\/(categories|products|search|schema|account|cart|category|product|my|profile|orders|order|basket)(\/|$)/.test(pathname) ||
            /^\/[^/]+\/schema\//.test(pathname) ||
            pathname.startsWith("/shop") ||
            pathname.startsWith("/showroom") ||
            pathname.startsWith("/customer") ||
            hasJwtRole("Customer");

          redirectTo(pathname.startsWith("/agency") || requestUrl.startsWith("/catalog/agency-")
            ? "/login"
            : isCustomerContext
            ? "/login"
            : "/admin/login");
        }

        return Promise.reject(error);
      }
    );
  }

  private getLocalBaseURL(baseURL: string): string {
    return baseURL.replace(/\/+$/, "");
  }

  public async agencyBootstrap(): Promise<AgencyBootstrap> {
    const response = await this.instance.get("/catalog/agency-account/bootstrap");
    catalogCsrfToken = response.data.csrfToken;
    return response.data;
  }

  private async catalogCsrf(): Promise<string> {
    if (catalogCsrfToken) return catalogCsrfToken;
    if (!catalogCsrfPromise) {
      catalogCsrfPromise = this.agencyBootstrap().then(function (bootstrap) {
        return bootstrap.csrfToken;
      }).finally(function () {
        catalogCsrfPromise = null;
      });
    }
    return catalogCsrfPromise;
  }

  public async get(endpoint: string): Promise<any> {
    try {
      const response = await this.instance.get(`${endpoint}`, {
        "Access-Control-Allow-Origin": "*",
      });
      return response.data;
    } catch (error: any) {
      AppFeedback.httpError(error);
    }
  }

  public async getasync(endpoint: string): Promise<any> {
    return await this.instance.get(`${endpoint}`);
  }
  public async getResource(endpoint: string, signal?: AbortSignal, responseType: "json" | "blob" = "json"): Promise<any> {
    const response = await this.instance.get(endpoint, { signal, responseType });
    return response.data;
  }
  public async getAnonymousResource(endpoint: string, signal?: AbortSignal, responseType: "json" | "blob" = "json"): Promise<any> {
    const response = await this.instance.get(endpoint, { signal, responseType, withCredentials: false });
    return response.data;
  }
  public async getblob(endpoint: string, signal?: AbortSignal): Promise<any> {
    return await this.instance.get(`${endpoint}`, { responseType: "blob", signal });
  }

  public async getwithparam(
    endpoint: string,
    params?: Record<string, any>
  ): Promise<any> {
    try {
      var request = await this.instance.get(`${endpoint}`, params);
      return request.data;
    } catch (error: any) {
      AppFeedback.httpError(error);
    }
  }

  public async postasync(endpoint: string, data: any, options?: { headers?: Record<string, string> }): Promise<any> {
    return await this.instance.post(`${endpoint}`, data, options);
  }

  public async putasync(endpoint: string, data: unknown, options?: { headers?: Record<string, string>; signal?: AbortSignal }): Promise<any> {
    return await this.instance.put(endpoint, data, options);
  }

  public async submitFrom(
    endpoint: string,
    form: FormInstance,
    callBack?: Function
  ): Promise<void> {
    const formValues = form.getFieldsValue();
    try {
      await this.postasync(endpoint, formValues);

      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });

      form.resetFields();
      callBack && callBack();
    } catch (error) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async submitFrom1(
    endpoint: string,
    form: FormData,
    callBack?: Function
  ): Promise<void> {
    try {
      await this.postasync(endpoint, form);

      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });

      callBack && callBack();
    } catch (error) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async submitFormDataPut(
    endpoint: string,
    form: FormData,
    callBack?: Function
  ): Promise<void> {
    try {
      await this.instance.put(endpoint, form);
      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });
      callBack && callBack();
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async submitFromNoResetFields(
    endpoint: string,
    form: FormInstance,
    callBack?: Function
  ): Promise<void> {
    const formValues = form.getFieldsValue();
    try {
      await this.postasync(endpoint, formValues);

      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });

      callBack && callBack();
    } catch (error) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async post(
    endpoint: string,
    data: any,
    callBack?: Function
  ): Promise<void> {
    try {
      await this.instance.post(`${endpoint}`, data, {
        headers: { "Content-Type": "application/json" },
      });

      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });
      callBack && callBack();
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async postWithResult(
    endpoint: string,
    data: any,
    callBack?: Function
  ): Promise<any> {
    try {
      const response = await this.instance.post(`${endpoint}`, data);

      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });

      callBack && callBack();

      return response;
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async postForData(endpoint: string, data: any): Promise<any> {
    try {
      const response = await this.instance.post(`${endpoint}`, data, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  /** multipart/form-data (e.g. support tickets); axios sets boundary automatically */
  public async postMultipart(endpoint: string, formData: FormData): Promise<any> {
    try {
      const response = await this.instance.post(`${endpoint}`, formData);
      return response.data;
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async putForData(endpoint: string, data: any): Promise<any> {
    try {
      const response = await this.instance.put(`${endpoint}`, data, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async put(
    endpoint: string,
    data: any,
    callBack?: Function
  ): Promise<boolean> {
    try {
      await this.instance.put(`${endpoint}`, data, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (callBack) callBack();
      void AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });
      return true;
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async patch(
    endpoint: string,
    data?: any
  ): Promise<any> {
    try {
      const response = await this.instance.patch(`${endpoint}`, data);
      return response.data;
    } catch (error: any) {
      AppFeedback.httpError(error);
      throw error;
    }
  }

  public async download(endpoint: string) {
    saveAs(`${this.baseurl}/${endpoint}`);
  }

  public async delete(endpoint: string, callBack?: Function): Promise<void> {
    try {
      await this.instance.delete(`${endpoint}`);
      await AppFeedback.success({
        icon: "success",
        title: t("عملیات با موفقیت انجام شد."),
        showConfirmButton: false,
        timer: 1500,
      });
      callBack && callBack();
    } catch (error: any) {
      await AppFeedback.httpError(error);
    }
  }

  /** DELETE without success toast — for background operations. */
  public async deleteSilent(endpoint: string, options?: { headers?: Record<string, string>; signal?: AbortSignal }): Promise<void> {
    await this.instance.delete(`${endpoint}`, options);
  }
}
